import { Clock, Mail, RotateCcw } from 'lucide-react';

import {
  DEFAULT_EMAIL_MESSAGE,
  DEFAULT_VALIDITY_MINUTES,
  MAX_EMAIL_MESSAGE_LENGTH,
  MAX_VALIDITY_MINUTES,
  MIN_VALIDITY_MINUTES,
  parseEmailVerificationStepSettings,
  writeEmailVerificationStepSettings,
} from '../../lib/emailVerificationStepSettings';
import type { WorkflowStepDto } from '../../schemas/workflowSchemas';

/**
 * What an Email Verification step shows instead of the Method / Risk / Return Data tabs.
 *
 * This step calls no provider — WE email the code — so none of those tabs has anything to show,
 * exactly as with Questionnaire and Document Template.
 *
 * WHAT IS DELIBERATELY NOT HERE: code length, the attempt cap, the resend cool-down, and how the
 * code is stored. Those are security properties, fixed in the backend and identical to the mobile
 * OTP that guards the workflow link. Exposing them as settings would let a workflow be configured
 * into a weaker verification than the one this step claims to perform.
 */
export function EmailVerificationStepPanel({
  step,
  onUpdate,
  readOnly = false,
}: {
  step: WorkflowStepDto;
  onUpdate: (patch: Partial<WorkflowStepDto>) => void;
  readOnly?: boolean;
}) {
  const settings = parseEmailVerificationStepSettings(step.settings);

  // Null means "not configured", which the applicant will receive as the default — so the field
  // SHOWS the default rather than sitting empty and implying no message will be sent.
  const message = settings.emailVerificationMessage ?? DEFAULT_EMAIL_MESSAGE;
  const validity = settings.emailVerificationValidityMinutes ?? DEFAULT_VALIDITY_MINUTES;

  const isCustomMessage =
    settings.emailVerificationMessage !== null &&
    settings.emailVerificationMessage !== DEFAULT_EMAIL_MESSAGE;

  function patch(next: Parameters<typeof writeEmailVerificationStepSettings>[1]) {
    onUpdate({ settings: writeEmailVerificationStepSettings(step.settings, next) });
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EEF3FF] text-primary">
          <Mail size={18} />
        </span>
        <div>
          <h3 className="m-0 text-[length:var(--text-15)] font-semibold text-[#041A3A]">
            Email Verification
          </h3>
          <p className="m-0 mt-0.5 text-[length:var(--text-13)] leading-snug text-gray66">
            The applicant enters an email address, we send a 6-digit code, and they enter it back —
            confirming they can read that mailbox.
          </p>
        </div>
      </header>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="email-verification-message"
          className="text-[length:var(--text-13)] font-medium text-[#041A3A]"
        >
          Message in the email
        </label>
        <textarea
          id="email-verification-message"
          rows={3}
          maxLength={MAX_EMAIL_MESSAGE_LENGTH}
          disabled={readOnly}
          value={message}
          onChange={(e) => patch({ emailVerificationMessage: e.target.value })}
          className="w-full resize-y rounded-lg border border-grayEF bg-white px-3 py-2.5 text-[length:var(--text-13)] leading-relaxed text-[#041A3A] outline-none focus:border-primary disabled:bg-[#FAFAFA] disabled:text-gray66"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[length:var(--text-11)] text-gray66">
            Appears above the code. The code, the expiry line and the “never share this” warning are
            always included.
          </span>
          <span className="shrink-0 text-[length:var(--text-11)] text-gray66">
            {message.length}/{MAX_EMAIL_MESSAGE_LENGTH}
          </span>
        </div>

        {/* Only offered once the copy actually differs — a reset button that resets to what is
            already there is a dead control. */}
        {isCustomMessage && !readOnly && (
          <button
            type="button"
            onClick={() => patch({ emailVerificationMessage: null })}
            className="mt-1 flex items-center gap-1.5 self-start text-[length:var(--text-12)] font-medium text-primary hover:underline"
          >
            <RotateCcw size={13} />
            Reset to the default message
          </button>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="email-verification-validity"
          className="flex items-center gap-1.5 text-[length:var(--text-13)] font-medium text-[#041A3A]"
        >
          <Clock size={14} className="text-gray66" />
          Code expires after
        </label>
        <div className="flex items-center gap-2">
          <input
            id="email-verification-validity"
            type="number"
            inputMode="numeric"
            min={MIN_VALIDITY_MINUTES}
            max={MAX_VALIDITY_MINUTES}
            disabled={readOnly}
            value={validity}
            onChange={(e) => {
              const raw = Number(e.target.value);
              // Blank or non-numeric clears back to "not configured" rather than writing NaN, which
              // would fail the schema on the next read and silently reset everything in the blob.
              if (!Number.isFinite(raw)) {
                patch({ emailVerificationValidityMinutes: null });
                return;
              }
              patch({
                emailVerificationValidityMinutes: Math.min(
                  Math.max(Math.trunc(raw), MIN_VALIDITY_MINUTES),
                  MAX_VALIDITY_MINUTES,
                ),
              });
            }}
            className="w-20 rounded-lg border border-grayEF bg-white px-3 py-2 text-[length:var(--text-13)] text-[#041A3A] outline-none focus:border-primary disabled:bg-[#FAFAFA] disabled:text-gray66"
          />
          <span className="text-[length:var(--text-13)] text-gray66">minutes</span>
        </div>
        <span className="text-[length:var(--text-11)] text-gray66">
          Between {MIN_VALIDITY_MINUTES} and {MAX_VALIDITY_MINUTES} minutes. Shorter is safer; too
          short and the applicant can’t switch to their mail app and back in time.
        </span>
      </div>
    </div>
  );
}
